import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const CLIENT_TYPES = ['Income Tax', 'GST & PT', 'Business', 'Consulting'];

export default function ClientFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: '', company_name: '', address: '', pincode: '',
    contact_number: '', email: '', gst_number: '', personal_pan: '',
    company_pan: '', tan_number: '', client_types: []
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/clients/${id}`).then(({ data }) => {
        setForm({
          full_name: data.full_name || '', company_name: data.company_name || '',
          address: data.address || '', pincode: data.pincode || '',
          contact_number: data.contact_number || '', email: data.email || '',
          gst_number: data.gst_number || '', personal_pan: data.personal_pan || '',
          company_pan: data.company_pan || '', tan_number: data.tan_number || '',
          client_types: data.client_types || []
        });
      }).catch(() => toast.error('Failed to load client'));
    }
  }, [id, isEdit]);

  const handleTypeToggle = (type) => {
    setForm(prev => ({
      ...prev,
      client_types: prev.client_types.includes(type)
        ? prev.client_types.filter(t => t !== type)
        : [...prev.client_types, type]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error('Full name is required'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/clients/${id}`, form);
        toast.success('Client updated');
      } else {
        await api.post('/clients', form);
        toast.success('Client created');
      }
      navigate('/admin/clients');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  const f = (name) => form[name];
  const setF = (name, val) => setForm(prev => ({ ...prev, [name]: val }));

  return (
    <div data-testid="client-form-page">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/admin/clients')} className="p-2 hover:bg-olive-50 rounded-sm" data-testid="back-btn">
          <ArrowLeft size={20} className="text-olive-600" />
        </button>
        <div>
          <h1 className="font-heading text-4xl font-semibold text-[#111B11] tracking-tight">
            {isEdit ? 'Edit Client' : 'New Client'}
          </h1>
          <p className="text-sm text-[#4F734F] mt-1">
            {isEdit ? 'Update client information' : 'Register a new client'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white border border-[#E6EDE6] rounded-sm p-6 lg:p-8">
        {/* Client Types */}
        <div className="mb-8">
          <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F] mb-3 block">Client Types</Label>
          <div className="flex flex-wrap gap-4">
            {CLIENT_TYPES.map(type => (
              <label key={type} className="flex items-center gap-2 cursor-pointer" data-testid={`client-type-${type.toLowerCase().replace(/\s+/g, '-')}`}>
                <Checkbox
                  checked={form.client_types.includes(type)}
                  onCheckedChange={() => handleTypeToggle(type)}
                />
                <span className="text-sm text-[#273A27]">{type}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Full Name</Label>
            <Input value={f('full_name')} onChange={(e) => setF('full_name', e.target.value)} className="input-field" placeholder="Enter full name" required data-testid="client-full_name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Company Name</Label>
            <Input value={f('company_name')} onChange={(e) => setF('company_name', e.target.value)} className="input-field" placeholder="Enter company name" data-testid="client-company_name-input" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Address</Label>
            <Textarea value={f('address')} onChange={(e) => setF('address', e.target.value)} className="input-field min-h-[80px]" placeholder="Enter full address" data-testid="client-address-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Pincode</Label>
            <Input value={f('pincode')} onChange={(e) => setF('pincode', e.target.value)} className="input-field" placeholder="Enter pincode" data-testid="client-pincode-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Contact Number</Label>
            <Input value={f('contact_number')} onChange={(e) => setF('contact_number', e.target.value)} className="input-field" placeholder="Enter contact number" data-testid="client-contact_number-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Email Address</Label>
            <Input value={f('email')} onChange={(e) => setF('email', e.target.value)} className="input-field" type="email" placeholder="Enter email" data-testid="client-email-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">GST Number</Label>
            <Input value={f('gst_number')} onChange={(e) => setF('gst_number', e.target.value)} className="input-field" placeholder="e.g., 29AABCT1332L1ZH" data-testid="client-gst_number-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Personal PAN</Label>
            <Input value={f('personal_pan')} onChange={(e) => setF('personal_pan', e.target.value)} className="input-field" placeholder="e.g., ABCDE1234F" data-testid="client-personal_pan-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">Company PAN</Label>
            <Input value={f('company_pan')} onChange={(e) => setF('company_pan', e.target.value)} className="input-field" placeholder="e.g., AABCT1332L" data-testid="client-company_pan-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs tracking-wide uppercase font-semibold text-[#4F734F]">TAN Number</Label>
            <Input value={f('tan_number')} onChange={(e) => setF('tan_number', e.target.value)} className="input-field" placeholder="e.g., BLRT12345A" data-testid="client-tan_number-input" />
          </div>
        </div>

        <div className="flex items-center gap-3 mt-8 pt-6 border-t border-[#E6EDE6]">
          <Button type="submit" disabled={loading} className="btn-primary gap-2" data-testid="save-client-btn">
            <Save size={16} /> {loading ? 'Saving...' : isEdit ? 'Update Client' : 'Create Client'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/admin/clients')} data-testid="cancel-btn">
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
